<template>
  <div v-if="displayItems.length" class="media-carousel" role="region" :aria-label="label" @keydown.left.self.prevent="go(-1)" @keydown.right.self.prevent="go(1)" tabindex="0">
    <button
      type="button"
      class="media-carousel__stage"
      aria-label="Abrir imagen ampliada"
      @click="expandable && expand()"
      @keydown.enter.prevent="expandable && expand()"
      @keydown.space.prevent="expandable && expand()">
      <img
        v-if="active.type !== 'video' && !active.fallback"
        class="media-carousel__stage-background"
        :src="cldOptimized(active.url, IMG.stage)"
        alt=""
        aria-hidden="true"
        @error="markImageFailed(active.url, $event)">
      <VideoPreview v-if="active.type === 'video' && !expanded" :key="active.url" :src="active.url" :poster="active.poster" :title="active.description || label" />
      <span v-else-if="active.type === 'video'">Video abierto en vista ampliada</span>
      <img v-else :key="active.url" class="media-carousel__stage-image" :class="{ 'media-carousel__stage-image--fallback': active.fallback }" :src="cldOptimized(active.url, IMG.stage)" :alt="active.description || label" decoding="async" @error="markImageFailed(active.url, $event)" />
      <span class="media-carousel__expand-icon" aria-hidden="true">⤢</span>
    </button>
    <Teleport to="body">
      <dialog v-if="expandable" ref="dialog" class="media-dialog" :aria-label="label" @close="expanded = false" @click.self="close">
        <button type="button" class="media-dialog__close" @click="close">Cerrar ×</button>
        <div class="media-dialog__viewer">
          <div
            class="media-dialog__stage"
            :class="{ 'media-dialog__stage--zoomed': zoomed }"
            @mousemove="updateZoomOrigin"
            @mouseleave="disableZoom">
            <VideoPreview v-if="active.type === 'video'" :key="active.url" :src="active.url" :poster="active.poster" :title="active.description || label" />
            <img
              v-else
              :key="active.url"
              class="media-dialog__image"
              :class="{ 'media-dialog__image--fallback': active.fallback }"
              :src="cldOptimized(active.url, IMG.stage)"
              :alt="active.description || label"
              :style="zoomStyle"
              decoding="async"
              @mouseenter="enableZoom"
              @error="markImageFailed(active.url, $event)" />
            <span v-if="active.type !== 'video'" class="media-dialog__zoom-hint">Pasá el mouse para ampliar</span>
          </div>
          <div v-if="displayItems.length > 1" class="media-carousel__thumbs" aria-label="Seleccionar imagen o video">
            <button
              v-for="(item, i) in displayItems"
              :key="`${item.url}-${i}`"
              type="button"
              :aria-label="`${item.type === 'video' ? 'Video' : 'Imagen'} ${i + 1}`"
              :aria-pressed="index === i"
              :class="{ active: index === i }"
              @click="index = i; disableZoom()">
              <span v-if="item.type === 'video'" class="media-carousel__video-thumb"><span aria-hidden="true">▶</span> Video {{ i + 1 }}</span>
              <img v-else :src="cldOptimized(item.url, IMG.thumb)" :class="{ 'media-carousel__thumb-image--fallback': item.fallback }" alt="" loading="lazy" @error="markImageFailed(item.url)" />
            </button>
          </div>
        </div>
      </dialog>
    </Teleport>
    <p v-if="active.description" class="media-carousel__caption">{{ active.description }}</p>
    <div v-if="displayItems.length > 1" class="media-carousel__thumbs" aria-label="Seleccionar imagen o video">
      <button v-for="(item, i) in displayItems" :key="`${item.url}-${i}`" type="button" :aria-label="`${item.type === 'video' ? 'Video' : 'Imagen'} ${i + 1}`"
        :aria-pressed="index === i" :class="{ active: index === i }" @click="index = i">
        <span v-if="item.type === 'video'" class="media-carousel__video-thumb"><span aria-hidden="true">▶</span> Video {{ i + 1 }}</span>
        <img v-else :src="cldOptimized(item.url, IMG.thumb)" :class="{ 'media-carousel__thumb-image--fallback': item.fallback }" alt="" loading="lazy" @error="markImageFailed(item.url)" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import VideoPreview from './VideoPreview.vue';
import { cldOptimized, IMG } from '@/utils/cloudinary';
const props = defineProps({ items: { type: Array, default: () => [] }, label: { type: String, default: 'Galería multimedia' }, expandable: { type: Boolean, default: true }, initialIndex: { type: Number, default: 0 }, fallbackUrl: { type: String, default: '' } });
const index = ref(props.initialIndex);
const failedImages = ref([]);
const displayItems = computed(() => {
  const available = props.items.filter(item => item.type === 'video' || !failedImages.value.includes(item.url));
  if (props.fallbackUrl && !available.some(item => item.type === 'image')) {
    available.unshift({ url: props.fallbackUrl, type: 'image', fallback: true });
  }
  return available;
});
function markImageFailed(url, event) {
  if (!url || url === props.fallbackUrl || failedImages.value.includes(url)) return;
  const attempted = event?.currentTarget?.getAttribute('src');
  if (attempted && attempted !== cldOptimized(url, IMG.stage) && attempted !== cldOptimized(url, IMG.thumb)) return;
  failedImages.value = [...failedImages.value, url];
  index.value = 0;
  disableZoom();
}
function go(direction) {
  if (displayItems.value.length) index.value = (index.value + direction + displayItems.value.length) % displayItems.value.length;
}
const expanded = ref(false), dialog = ref(null);
const zoomed = ref(false);
const zoomOrigin = ref({ x: 50, y: 50 });
async function expand() { expanded.value = true; await nextTick(); dialog.value?.showModal(); }
function close() { dialog.value?.close(); expanded.value = false; disableZoom(); }
function enableZoom() { zoomed.value = true; }
function disableZoom() { zoomed.value = false; zoomOrigin.value = { x: 50, y: 50 }; }
function updateZoomOrigin(event) {
  const rect = event.currentTarget.getBoundingClientRect();
  zoomOrigin.value = {
    x: ((event.clientX - rect.left) / rect.width) * 100,
    y: ((event.clientY - rect.top) / rect.height) * 100
  };
}
const zoomStyle = computed(() => ({
  '--zoom-x': `${zoomOrigin.value.x}%`,
  '--zoom-y': `${zoomOrigin.value.y}%`
}));
onBeforeUnmount(close);
const active = computed(() => displayItems.value[index.value] || displayItems.value[0] || {});
watch(() => props.items, () => { failedImages.value = []; index.value = 0; });
</script>

<style scoped>
.media-carousel { width: 100%; min-width: 0; color: #fff; }
.media-carousel__stage {
  width: 100%;
  height: clamp(360px, 52vw, 680px);
  background: #141414;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  cursor: zoom-in;
  position: relative;
}
.media-carousel__stage::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  opacity: 0;
  transition: opacity 0.2s ease;
}
.media-carousel__stage:hover::after,
.media-carousel__stage:focus-visible::after { opacity: 1; }
.media-carousel__expand-icon {
  position: absolute;
  z-index: 1;
  color: #fff;
  font-size: 3.5rem;
  line-height: 1;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.media-carousel__stage:hover .media-carousel__expand-icon,
.media-carousel__stage:focus-visible .media-carousel__expand-icon {
  opacity: 1;
  transform: scale(1);
}
.media-carousel__stage > img { width: 100%; height: 100%; object-fit: contain; }
.media-carousel__stage-background {
  position: absolute;
  inset: -24px;
  width: calc(100% + 48px) !important;
  height: calc(100% + 48px) !important;
  object-fit: cover !important;
  filter: blur(18px) brightness(0.42);
  opacity: 0.95;
  z-index: 0;
  pointer-events: none;
}
.media-carousel__stage-image { position: relative; z-index: 1; }
.media-carousel__stage-image--fallback { padding: 2rem; }
.media-carousel button { border: 1px solid #ffffff45; border-radius: 0; background: #202020; color: #fff; min-width: 44px; min-height: 44px; }
.media-carousel button:focus-visible, .media-carousel button.active { outline: 2px solid var(--primary-color, #ffe000); outline-offset: 2px; }
.media-carousel__thumbs { display: flex; overflow-x: auto; gap: 12px; padding: 5px; scrollbar-width: thin; }
.media-carousel__thumbs button { flex: 0 0 84px; width: 84px; height: 84px; overflow: hidden; padding: 0; }
.media-carousel__thumbs img { width: 100%; height: 100%; object-fit: cover; }
.media-carousel__thumbs img.media-carousel__thumb-image--fallback { object-fit: contain; padding: 0.5rem; }
.media-carousel__video-thumb { display: flex; flex-direction: column; gap: 5px; font-size: .75rem; }
.media-carousel__video-thumb > span { color: var(--primary-color, #ffe000); font-size: 20px; }
.media-carousel__caption { text-align: center; color: #ccc; overflow-wrap: anywhere; }
.media-dialog { width: min(1400px, 96vw); max-height: 96dvh; border: 1px solid #555; border-radius: 0; background: #171717; color: #fff; padding: 20px; }
.media-dialog::backdrop { background: #000c; }
.media-dialog__close { display: block; margin: 0 0 16px auto; padding: 10px 16px; }
.media-dialog__stage {
  height: min(78dvh, 900px);
  overflow: hidden;
  background: #0d0d0d;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-in;
}
.media-dialog__stage--zoomed { cursor: zoom-out; }
.media-dialog__image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.15s ease;
  transform-origin: var(--zoom-x, 50%) var(--zoom-y, 50%);
}
.media-dialog__image--fallback { padding: 2rem; }
.media-dialog__stage--zoomed .media-dialog__image { transform: scale(2.2); }
.media-dialog__zoom-hint {
  position: absolute;
  bottom: 14px;
  padding: 6px 10px;
  border-radius: 0;
  color: #fff;
  background: #0009;
  font-size: .8rem;
  pointer-events: none;
  opacity: .8;
}
.media-dialog__viewer { min-width: 0; }

@media (max-width: 767.98px) {
  .media-carousel__stage { height: min(78vw, 500px); min-height: 280px; }
  .media-carousel__thumbs button { flex-basis: 72px; width: 72px; height: 72px; }
}
</style>
